import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IProfileFieldConstraintsMap, WorkerProfileField } from 'types/profileCustomFields';
import { getCreateWorkerParams } from './utils';
import { createWorker, fetchAllCertifications, fetchCertificationGroups } from 'modules/state-mgmt/onboarding';
import { ROUTES } from 'constants/routes';
import { prettifyErrorResponse, extractNationalNumber } from 'utils';
import { enqueueSnackbar } from 'notistack';

interface IUseCreateWorker {
  onSuccess?: () => void;
  onFailure?: (backendErrors: any) => void;
}

export const useFieldConstraints = (
  fieldsConstraints: IProfileFieldConstraintsMap,
  canCreateUnverifiedWorkers: boolean,
) => {
  return useMemo(() => {
    const emailConstraints = fieldsConstraints?.[WorkerProfileField?.email];
    const phoneConstraints = fieldsConstraints?.[WorkerProfileField?.mobilePhoneNumber];
    const eitherEmailOrPhoneConstraints = fieldsConstraints?.[WorkerProfileField?.emailOrMobilePhoneNumber];

    const isEmailVisible = emailConstraints?.isVisible;
    const isPhoneVisible = phoneConstraints?.isVisible;
    const isEitherEmailOrPhoneVisible = eitherEmailOrPhoneConstraints?.isVisible;

    const neitherEmailOrPhoneMandatory = !emailConstraints?.isMandatory && !phoneConstraints?.isMandatory;

    // Email field becomes mandatory if any of these conditions are met:
    // 1. Email is explicitly marked as mandatory in constraints
    // 2. Either email or phone is required, but phone isn't mandatory (forcing email to be mandatory)
    // 3. When unverified workers aren't allowed AND:
    //    - Phone isn't mandatory AND
    //    - Either the email field is visible OR the combined field is visible
    const isEmailMandatory =
      emailConstraints?.isMandatory ||
      (eitherEmailOrPhoneConstraints?.isMandatory && !phoneConstraints?.isMandatory) ||
      (!canCreateUnverifiedWorkers &&
        !phoneConstraints?.isMandatory &&
        (isEmailVisible || isEitherEmailOrPhoneVisible));

    // Taking reference from isEmailMandatory there is almost same conditions but for phone
    const isPhoneMandatory =
      phoneConstraints?.isMandatory ||
      (eitherEmailOrPhoneConstraints?.isMandatory && !emailConstraints?.isMandatory) ||
      (!canCreateUnverifiedWorkers &&
        !emailConstraints?.isMandatory &&
        (isPhoneVisible || isEitherEmailOrPhoneVisible));

    // This becomes true when:
    // 1. Neither email nor phone are individually mandatory AND either:
    //    a. The combined field is explicitly marked as mandatory in constraints
    //    b. The system doesn't allow unverified workers (requiring at least one contact method)
    const isEitherPhoneOrEmailMandatory =
      neitherEmailOrPhoneMandatory &&
      (eitherEmailOrPhoneConstraints?.isMandatory ||
        !canCreateUnverifiedWorkers ||
        (eitherEmailOrPhoneConstraints?.isVisible && canCreateUnverifiedWorkers));

    const bothVisible = (isEmailVisible && isPhoneVisible) || isEitherEmailOrPhoneVisible;

    return {
      isEmailMandatory,
      isPhoneMandatory,
      isEmailVisible,
      isPhoneVisible,
      isEitherEmailOrPhoneVisible,
      isOrTextVisible: isEitherPhoneOrEmailMandatory && bothVisible,
    };
  }, [fieldsConstraints, canCreateUnverifiedWorkers]);
};

export const useCreateWorker = ({ onSuccess, onFailure }: IUseCreateWorker) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { onboardingTemp, languageCode } = useAppSelector(state => ({
    onboardingTemp: state.onboarding.temp || {},
    languageCode: state.app?.languageCode,
  }));
  const handleCreateWorker = (values: any) => {
    dispatch(createWorker(getCreateWorkerParams({ ...(onboardingTemp || {}), ...values, languageCode }))).then(
      async res => {
        if (res.type.includes('fulfilled')) {
          onSuccess?.();
          if (values.email || extractNationalNumber(values.mobilePhoneNumber)) {
            navigate(ROUTES.CREATE_PASSWORD.path);
          } else {
            try {
              await Promise.all([
                dispatch(
                  fetchCertificationGroups({
                    projectId: onboardingTemp?.project?.value,
                    workerId: res.payload.id,
                    languageCode,
                  }),
                ).unwrap(),
                await dispatch(fetchAllCertifications({ workerId: res.payload.id, languageCode })).unwrap(),
              ]);

              navigate(ROUTES.TERMS_AND_CONDITIONS.path);
            } catch (error) {
              enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
            }
          }
        } else {
          if (res.payload?.message) {
            enqueueSnackbar(prettifyErrorResponse(res.payload), { variant: 'error' });
            return;
          }
          let backendErrors = {};
          Object.keys(res.payload).forEach(key => {
            backendErrors[key] = {
              type: 'backendError',
              message: res.payload?.[key]?.join?.(' '),
            };
          });
          onFailure?.(backendErrors);
        }
      },
    );
  };
  return { handleCreateWorker };
};
